import { useEffect, useState } from "react"

import type { BookableService } from "../../api/journal-actions.api"
import { fetchBookingCatalog } from "../../api/journal-actions.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"

/**
 * Услуги, которые делает ЭТОТ мастер, — для формы 5-М8.
 *
 * ПОЧЕМУ ЭТО ДВА ЗАПРОСА И ПОЧЕМУ ОНИ ЗДЕСЬ. Маршрута «мои услуги» в API нет: перечень
 * услуг лежит в каталоге (GET /v1/cabinet/catalog, право catalog.view), а связка
 * «кто что делает» — в списке сотрудников (GET /v1/cabinet/catalog/employees, право
 * staff.view, поле service_ids). Оба запроса делает fetchBookingCatalog; здесь остаётся
 * пересечение и два состояния отказа, которые нельзя путать.
 *
 * ДВА РАЗНЫХ «УСЛУГ НЕТ», И ИХ НЕЛЬЗЯ СВОДИТЬ В ОДНО:
 *   · каталог не отдался (у мастера нет права) — это «форма сейчас не работает»,
 *     и человеку нужно сказать, к кому идти;
 *   · каталог отдался, но у мастера не отмечено ни одной услуги — это настройка салона
 *     («У Дмитрия услуги не отмечены» в чек-листе 4-А2), и это тоже не сбой формы.
 * Один общий текст «услуг нет» отправил бы мастера чинить не то.
 *
 * ПРО ЦЕНЫ. Каталог отдаёт price_minor всем, у кого есть catalog.view, независимо
 * от настройки «деньги видны мастеру»: гашения в этом ответе нет (дефект вынесен
 * в отчёт). Поэтому цена из каталога ПОКАЗЫВАЕТСЯ ТОЛЬКО тогда, когда деньги мастеру
 * открыты по его же дню, — решение принимает экран, а не этот хук.
 */

export interface MasterServices {
  readonly items: readonly BookableService[]
  readonly loading: boolean
  /** Каталог не загрузился: нет права либо нет связи. */
  readonly problem: ApiProblem | null
  /** Каталог загружен, но мастеру не отмечено ни одной услуги. */
  readonly noneAssigned: boolean
}

export function useMasterServices(employeeId: string | null): MasterServices {
  const [state, setState] = useState<MasterServices>({
    items: [],
    loading: employeeId !== null,
    problem: null,
    noneAssigned: false,
  })

  useEffect(() => {
    if (employeeId === null) {
      setState({ items: [], loading: false, problem: null, noneAssigned: false })
      return undefined
    }

    const controller = new AbortController()
    setState((previous) => ({ ...previous, loading: true, problem: null }))

    const run = async (): Promise<void> => {
      try {
        const catalog = await fetchBookingCatalog(controller.signal)
        if (controller.signal.aborted) return
        const me = catalog.employees.find((item) => item.id === employeeId)
        const mine = new Set(me?.serviceIds ?? [])
        const items = catalog.services.filter((service) => mine.has(service.id))
        setState({
          items,
          loading: false,
          problem: null,
          noneAssigned: items.length === 0,
        })
      } catch (error) {
        if (controller.signal.aborted) return
        setState({
          items: [],
          loading: false,
          problem: isApiProblem(error) ? error : transportProblem("unknown"),
          noneAssigned: false,
        })
      }
    }
    void run()

    return () => {
      controller.abort()
    }
  }, [employeeId])

  return state
}
